import { createStore } from 'zustand/vanilla'
import { devtools } from 'zustand/middleware'
import {
  EMPTY_DOCUMENT_IDS,
  UNSCOPED_DOCUMENTS_LOAD_KEY,
} from './documents-store-constants'
import { importDocumentsAction } from './documents-store-import'
import {
  loadAllDocumentsAction,
  loadDocumentByIdAction,
  loadDocumentsAction,
} from './documents-store-load'
import {
  applyDocumentAddedState,
  applyDocumentRemovedByIdState,
  applyDocumentRemovedState,
  upsertDocumentInItems,
} from './documents-store-state'
import { createDocumentAction } from './documents-store-create'
import { subscribeToAllDocumentChanges } from './documents-store-sync'
import { updateDocumentAction } from './documents-store-update'
import type { AllDocumentChange } from './documents-store-sync'
import type { StoreApi } from 'zustand/vanilla'
import type { Dependencies } from '../../types/Dependencies'
import type { CollectionDoc, Doc } from '../../types/Doc'
import type { PreparedImportRequest } from '@/shared/lib/documentImport'

export type ImportResultSummary = {
  createdFieldCount: number
  importedRowCount: number
  skippedColumnCount: number
}

export type DocumentsLoadStatus = 'idle' | 'loading' | 'succeeded' | 'failed'

export type DocumentsState = {
  items: Array<Doc>
  newDocumentIdsByCollectionId: Record<string, Array<string>>
  loadStatusByCollectionId: Record<string, DocumentsLoadStatus>
  loadErrorByCollectionId: Record<string, string | null>
  importStatus: 'idle' | 'submitting' | 'succeeded' | 'failed'
  importError: string | null
  lastImportResult: ImportResultSummary | null
}

export type DocumentsActions = {
  load: (payload?: DocumentsLoadRequest) => Promise<void>
  loadById: (documentId: string) => Promise<Doc>
  loadAllOnce: (payload?: DocumentsLoadAllRequest) => Promise<void>
  setDocuments: (payload: { docs: Array<CollectionDoc | Doc> }) => void
  startSync: () => void
  stopSync: () => void
  applyDocumentAdded: (payload: { document: Doc }) => void
  applyDocumentUpdated: (payload: { document: Doc }) => void
  applyDocumentRemoved: (payload: {
    documentCollectionId: string
    documentId: string
  }) => void
  createDocument: (request: CreateDocumentRequest) => Promise<Doc>
  updateDocument: (request: UpdateDocumentRequest) => Promise<Doc>
  importDocuments: (request: PreparedImportRequest) => Promise<void>
  resetImportState: () => void
}

export type DocumentsLoadRequest = {
  documentCollectionId?: string
  setLoadState?: boolean
}

export type DocumentsLoadAllRequest = {
  force?: boolean
  setLoadState?: boolean
}

export type UpdateDocumentRequest = {
  document: Doc
  values: Record<string, string>
}

export type CreateDocumentRequest = {
  documentCollectionId: string
  values: Record<string, string>
}

export type DocumentsStore = DocumentsState & DocumentsActions

export type DocumentsStoreApi = StoreApi<DocumentsStore>

type DocumentsGetState = DocumentsStoreApi['getState']
export type DocumentsSetState = DocumentsStoreApi['setState']

export type DocumentsImportState = {
  status: DocumentsState['importStatus']
  error: string | null
  result: ImportResultSummary | null
}

export type ImportDocumentStoreActions = Pick<
  DocumentsStore,
  'importDocuments' | 'resetImportState'
>

export function createDocumentsStore(dependencies: Dependencies) {
  return createStore<DocumentsStore>()(
    devtools(
      (set, get) => ({
        ...createInitialDocumentsState(),
        ...createDocumentsActions({ dependencies, get, set }),
      }),
      { name: 'DocumentsStore', enabled: import.meta.env.DEV },
    ),
  )
}

function setDocumentsState(
  set: DocumentsSetState,
  docs: Array<CollectionDoc | Doc>,
) {
  set({ items: docs })
}

function applyDocumentUpdatedState(set: DocumentsSetState, document: Doc) {
  set((state) => ({
    items: upsertDocumentInItems(state.items, document),
  }))
}

function createDocumentsActions({
  dependencies,
  get,
  set,
}: {
  dependencies: Dependencies
  get: DocumentsGetState
  set: DocumentsSetState
}): DocumentsActions {
  let stopSync: (() => void) | null = null

  const loadAllOnce = createLoadAllOnceAction({ dependencies, get, set })

  return {
    load: (payload) => loadDocumentsAction({ dependencies, payload, set }),
    loadById: (documentId) =>
      loadDocumentByIdAction({ dependencies, documentId, set }),
    loadAllOnce,
    setDocuments: ({ docs }) => {
      setDocumentsState(set, docs)
    },
    startSync: () => {
      stopSync ??= subscribeToAllDocumentChanges({
        records: dependencies.records,
        onChange: (change) => {
          applyAllDocumentChange({ change, set })
        },
        onError: (error) => {
          console.error('Failed to subscribe to document changes.', error)
        },
      })
    },
    stopSync: () => {
      stopSync?.()
      stopSync = null
    },
    applyDocumentAdded: ({ document }) => {
      set((state) => applyDocumentAddedState({ document, state }))
    },
    applyDocumentUpdated: ({ document }) => {
      applyDocumentUpdatedState(set, document)
    },
    applyDocumentRemoved: ({ documentCollectionId, documentId }) => {
      set((state) =>
        applyDocumentRemovedState({
          documentCollectionId,
          documentId,
          state,
        }),
      )
    },
    createDocument: (request) =>
      createDocumentAction({ dependencies, request, set }),
    updateDocument: (request) =>
      updateDocumentAction({ dependencies, get, request, set }),
    importDocuments: (request) =>
      importDocumentsAction({
        dependencies,
        loadDocuments: get().load,
        request,
        set,
      }),
    resetImportState: () => {
      set({
        importStatus: 'idle',
        importError: null,
        lastImportResult: null,
      })
    },
  }
}

function createLoadAllOnceAction({
  dependencies,
  get,
  set,
}: {
  dependencies: Dependencies
  get: DocumentsGetState
  set: DocumentsSetState
}) {
  let loadAllPromise: Promise<void> | null = null

  return (payload?: DocumentsLoadAllRequest) => {
    if (!payload?.force && hasLoadedOrStartedLoading(get())) {
      loadAllPromise ??= Promise.resolve()

      return loadAllPromise
    }

    loadAllPromise = loadAllDocumentsAction({
      dependencies,
      payload,
      set,
    }).finally(() => {
      loadAllPromise = null
    })

    return loadAllPromise
  }
}

function hasLoadedOrStartedLoading(state: DocumentsStore) {
  const loadState = selectDocumentsLoadState(state)

  return loadState.status === 'succeeded' || loadState.status === 'loading'
}

function applyAllDocumentChange({
  change,
  set,
}: {
  change: AllDocumentChange
  set: DocumentsSetState
}) {
  if (change.type === 'removed') {
    set((state) =>
      applyDocumentRemovedByIdState({
        documentId: change.documentId,
        state,
      }),
    )

    return
  }

  set((state) => ({
    items: upsertDocumentInItems(state.items, change.document),
  }))
}

export function createInitialDocumentsState(): DocumentsState {
  return {
    items: [],
    newDocumentIdsByCollectionId: {},
    loadStatusByCollectionId: {},
    loadErrorByCollectionId: {},
    importStatus: 'idle',
    importError: null,
    lastImportResult: null,
  }
}

export const selectDocuments = (state: DocumentsStore) => state.items

export function selectDocumentsByCollectionId(
  state: DocumentsStore | DocumentsState,
  documentCollectionId: string,
) {
  return state.items.filter(
    (document) => document.documentCollectionId === documentCollectionId,
  )
}

export function selectReferenceDocumentsByCollectionIds(
  state: DocumentsStore | DocumentsState,
  documentCollectionIds: Array<string>,
) {
  if (documentCollectionIds.length === 0) {
    return {}
  }

  const requestedCollectionIds = new Set(documentCollectionIds)
  const documentsByCollectionId = Object.fromEntries(
    documentCollectionIds.map((documentCollectionId) => [
      documentCollectionId,
      [] as Array<Doc>,
    ]),
  )

  for (const document of state.items) {
    if (!requestedCollectionIds.has(document.documentCollectionId)) {
      continue
    }

    documentsByCollectionId[document.documentCollectionId].push(document)
  }

  return documentsByCollectionId
}

export function selectNewDocumentIds(
  state: DocumentsStore | DocumentsState,
  documentCollectionId: string,
) {
  return (
    state.newDocumentIdsByCollectionId[documentCollectionId] ??
    EMPTY_DOCUMENT_IDS
  )
}

export function selectDocumentsLoadState(
  state: DocumentsStore | DocumentsState,
  documentCollectionId?: string,
) {
  const loadKey = getDocumentsLoadKey(documentCollectionId)

  return {
    status: state.loadStatusByCollectionId[loadKey] ?? 'idle',
    error: state.loadErrorByCollectionId[loadKey] ?? null,
  }
}

export function selectDocumentsImportState(
  state: DocumentsStore,
): DocumentsImportState {
  return {
    status: state.importStatus,
    error: state.importError,
    result: state.lastImportResult,
  }
}

function getDocumentsLoadKey(documentCollectionId?: string) {
  return documentCollectionId ?? UNSCOPED_DOCUMENTS_LOAD_KEY
}
